import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/errors';
import Icon from './Icon';

type Status = 'verifying' | 'success' | 'error';

// Mounted under both the logged-out and logged-in route trees (App.tsx) --
// the emailed link may be opened in a different browser/session than the
// one that signed up, so this can't assume either auth state.
const VerifyEmail: React.FC = () => {
  const { username, verifyEmail } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus('error');
      setError('This verification link is missing its token.');
      return;
    }

    (async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(apiErrorMessage(err, 'This verification link is invalid or has expired.'));
      }
    })();
  }, [token, verifyEmail]);

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-logo">
          <Icon name="pan" className="icon" /> RecipeEngine
        </h1>

        {status === 'verifying' && <p className="login-tagline">Verifying your email...</p>}

        {status === 'success' && (
          <>
            <p className="login-tagline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon name="check" className="icon" /> Your email is verified.
            </p>
            <Link to="/" className="btn-primary" style={{ textAlign: 'center' }}>
              {username ? 'Continue to RecipeEngine' : 'Sign in'}
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="login-error">{error}</div>
            <Link to="/" className="login-tagline">
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
